import { useEffect, useRef, useState } from 'react';
import twMerge from 'twMerge';

import dropdownStyles, { dropdownChevron, popupContainerStyles } from 'molecules/Dropdown/Dropdown.styles';
import DropdownItem from 'molecules/Dropdown/DropdownItem';
import Icon from 'molecules/Icon';

import type { VariantProps } from 'class-variance-authority';
import type { DropdownItemProps } from 'molecules/Dropdown/DropdownItem';
import type { IconIds } from 'molecules/Icon';
import type { FC, LegacyRef } from 'react';

interface DropdownProps extends VariantProps<typeof dropdownStyles> {
  startIcon?: IconIds | null;
  label: string;
  items?: DropdownItemProps[];
  onChange: (idx: number) => void;
  returnDefault?: boolean;
  defaultValue?: number;
  inputRef?: LegacyRef<HTMLButtonElement | HTMLInputElement>;
  popupClassName?: string;
}

const Dropdown: FC<DropdownProps> = ({
  type,
  startIcon,
  label,
  items,
  onChange,
  returnDefault,
  defaultValue = -1,
  inputRef,
  popupClassName,
  ...props
}) => {
  const [activeItem, setActiveItem] = useState(-1);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const labelContent =
    items && (type === 'input' || type === 'filter') && activeItem !== -1 ? items[activeItem].label : label;

  const setActiveFunction = (index: number) => {
    setActiveItem(index);
    onChange(index);
    setIsOpen(false);
  };

  useEffect(() => {
    returnDefault && setActiveItem(-1);
  }, [returnDefault]);

  useEffect(() => {
    setActiveItem(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef} data-cy="dropdown">
      <button
        data-cy="dropdown-btn"
        className={twMerge(dropdownStyles({ type }))}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        ref={inputRef as LegacyRef<HTMLButtonElement>}
        {...props}
      >
        {startIcon && <Icon icon={startIcon} size={20} aria-hidden={true} />}
        {labelContent}
        <Icon icon="chevron-up" size={20} className={twMerge(dropdownChevron({ isOpen, type }))} aria-hidden={true} />
      </button>

      <div className={twMerge(popupContainerStyles({ isOpen }), popupClassName)} data-cy="dropdown-popup">
        {items &&
          items.map((item, index) => (
            <DropdownItem
              dataCyAttribute={`item-${item?.label?.split(' ')?.join('-')?.toLowerCase() || index}`}
              anchorId={item?.id}
              key={item?.label || index}
              isActive={activeItem === index}
              onClick={e => {
                e.stopPropagation();
                setActiveFunction(index);
              }}
              {...item}
            />
          ))}
      </div>
    </div>
  );
};

export default Dropdown;
